import type { ActivityLog } from "@artisancode/api-types";

export const ENTITY_LABEL_MAP: Record<string, string> = {
  sale: "Penjualan",
  rider_sale: "Penjualan Rider",
  expense: "Pengeluaran",
  receivable_payment: "Pembayaran Piutang",
  product: "Produk",
  raw_material: "Bahan Baku",
  uom: "Satuan",
  unit_conversion: "Konversi Satuan",
  rider: "Rider",
  workspace: "Workspace",
  user: "User",
  role: "Role",
  bonus_rule: "Aturan Bonus",
  category_bonus_rule: "Aturan Bonus",
};

export function formatEntityName(name: string): string {
  return ENTITY_LABEL_MAP[name] || name;
}

export interface ActionMeta {
  label: string;
  badgeClass: string;
}

export const ACTION_META_MAP: Record<string, ActionMeta> = {
  create: {
    label: "Tambah",
    badgeClass:
      "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
  },
  update: {
    label: "Ubah",
    badgeClass:
      "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
  },
  delete: {
    label: "Hapus",
    badgeClass:
      "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20",
  },
  void: {
    label: "Batal (Void)",
    badgeClass:
      "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
  },
};

export function getActionMeta(action: string): ActionMeta {
  return (
    ACTION_META_MAP[action] || {
      label: action,
      badgeClass: "bg-muted text-muted-foreground border-border",
    }
  );
}

export function formatValueForDisplay(val: unknown): string {
  if (val === null || val === undefined) return "—";
  if (typeof val === "boolean") return val ? "Ya" : "Tidak";
  if (typeof val === "number") return val.toLocaleString("id-ID");
  if (typeof val === "object") {
    try {
      return JSON.stringify(val, null, 2);
    } catch {
      return String(val);
    }
  }
  return String(val);
}

export function formatDateWIB(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return new Intl.DateTimeFormat("id-ID", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Jakarta",
    }).format(d);
  } catch {
    return dateStr;
  }
}

export function getDateKeyWIB(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jakarta",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function getDayGroupLabel(
  dateStr: string,
  referenceDate: Date = new Date(),
): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;

    const targetKey = getDateKeyWIB(d);
    const todayKey = getDateKeyWIB(referenceDate);

    const yesterday = new Date(referenceDate);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = getDateKeyWIB(yesterday);

    if (targetKey === todayKey) return "Hari ini";
    if (targetKey === yesterdayKey) return "Kemarin";

    return new Intl.DateTimeFormat("id-ID", {
      dateStyle: "long",
      timeZone: "Asia/Jakarta",
    }).format(d);
  } catch {
    return dateStr;
  }
}

export function formatTimeWIB(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return (
      new Intl.DateTimeFormat("id-ID", {
        timeStyle: "short",
        timeZone: "Asia/Jakarta",
      }).format(d) + " WIB"
    );
  } catch {
    return dateStr;
  }
}

export function getActivitySummary(log: ActivityLog): string {
  const diffEntries = log.diff ? Object.keys(log.diff) : [];
  if (log.activity === "create") {
    return "Data dibuat";
  }
  if (log.activity === "delete") {
    return "Data dihapus";
  }
  if (log.activity === "void") {
    return "Transaksi dibatalkan (void)";
  }
  if (diffEntries.length > 0) {
    if (diffEntries.length <= 2) {
      return `Mengubah ${diffEntries.join(", ")}`;
    }
    return `${diffEntries.length} kolom diubah (${diffEntries.slice(0, 2).join(", ")}...)`;
  }
  return "Data diperbarui";
}

export interface ActivityGroup {
  dateLabel: string;
  items: ActivityLog[];
}

export function groupLogsByDay(
  logs: ActivityLog[],
  referenceDate: Date = new Date(),
): ActivityGroup[] {
  const groupMap = new Map<string, ActivityLog[]>();
  const groupOrder: string[] = [];

  for (const log of logs) {
    const label = getDayGroupLabel(log.createdAt, referenceDate);
    const existing = groupMap.get(label);
    if (existing) {
      existing.push(log);
    } else {
      groupMap.set(label, [log]);
      groupOrder.push(label);
    }
  }

  return groupOrder.map((label) => ({
    dateLabel: label,
    items: groupMap.get(label) ?? [],
  }));
}
